import React, { FC, RefObject, useLayoutEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { CosmosComponent } from '../../types';
import { append } from '../../utils';

import { useSizeObserver } from '../../customHooks';
import { TooltipPortal } from './TooltipPortal';

interface TooltipOverlayProps extends CosmosComponent {
  placement?: 'top' | 'bottom';
  displayAtMousePosition?: boolean;
  tooltipClassName?: string;
  mouseOffsetX: number;
  triggerRef: RefObject<HTMLDivElement>;
}

/**
 * It renders a tooltip inside a portal, based on mouse position, after a certain amount of milliseconds
 *
 * @prop {ReactNode} children tooltip content
 * @prop {'top' | 'bottom'} placement where the tooltip will be rendered
 * @prop {boolean} displayAtMousePosition indicates whether the tooltip should render
 * where the mouse cursor is or in the middle of its child element
 * @prop {string} tooltipClassName additional className added to the tooltip element
 * @prop {number} mouseOffsetX offsetX of the cursor on mouse hover
 * @prop {RefObject<HTMLDivElement>} triggerRef ref of the item that will trigger tooltip on mouseover
 */
export const TooltipOverlay: FC<TooltipOverlayProps> = ({
  children,
  placement = 'top',
  displayAtMousePosition,
  tooltipClassName,
  mouseOffsetX,
  triggerRef,
  testId,
}) => {
  const [tooltipCoordinates, setTooltipCoordinates] = useState({
    x: 0,
    y: 0,
  });
  const [tooltipPlacement, setTooltipPlacement] = useState(placement);

  const tooltipRef: RefObject<HTMLDivElement> = useRef(null);
  const childrenRef: RefObject<HTMLDivElement> = triggerRef;

  /** Observing Tooltip for height changes */
  const { height } = useSizeObserver(tooltipRef);

  const tooltipOffset = 5;

  /** Calculates coordinates and placement for the tooltip */
  const setCoordinatesAndPlacement = (
    tooltipRect: ClientRect,
    childrenRect: ClientRect,
  ) => {
    const innerHeight = window.innerHeight;

    /** Getting the space above the children element */
    const heightAboveChildren = childrenRect.top;
    /** Getting the space below the children element */
    const heightBelowChildren =
      innerHeight - childrenRect.top - childrenRect.height;

    let offsetY: number;
    let internalPlacement: 'top' | 'bottom';

    /** Defines the position of tooltip for top positioning */
    const offsetPlacementTop =
      childrenRect.top - tooltipRect.height - tooltipOffset;
    /** Defines the position of tooltip for bottom positioning */
    const offsetPlacementBottom =
      childrenRect.top + childrenRect.height + tooltipOffset;

    const tooltipTotalHeight = tooltipRect.height + tooltipOffset;

    if (placement === 'top') {
      offsetY = offsetPlacementTop;
      internalPlacement = 'top';

      /** Verify whether the tooltip fits above the children element, otherwise will place it below */
      if (
        heightAboveChildren < tooltipTotalHeight &&
        heightBelowChildren > tooltipTotalHeight
      ) {
        offsetY = offsetPlacementBottom;
        internalPlacement = 'bottom';
      }
    } else {
      offsetY = offsetPlacementBottom;
      internalPlacement = 'bottom';

      /** Verify whether the tooltip fits below the children element, otherwise will place it above */
      if (
        heightBelowChildren < tooltipTotalHeight &&
        heightAboveChildren > tooltipTotalHeight
      ) {
        offsetY = offsetPlacementTop;
        internalPlacement = 'top';
      }
    }

    const offsetX = displayAtMousePosition
      ? mouseOffsetX - tooltipRect.width / 2
      : childrenRect.left + childrenRect.width / 2 - tooltipRect.width / 2;

    setTooltipPlacement(internalPlacement);
    setTooltipCoordinates({
      x: offsetX,
      y: offsetY,
    });
  };

  /** Recalculates tooltip coordinates and placement on height updates */
  useLayoutEffect(() => {
    const tooltipRect =
      tooltipRef.current && tooltipRef.current.getBoundingClientRect();

    const childrenRect =
      childrenRef.current && childrenRef.current.getBoundingClientRect();

    if (!height || !tooltipRect || !childrenRect) {
      return;
    }

    setCoordinatesAndPlacement(tooltipRect, childrenRect);
  }, [height]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TooltipPortal>
      <StyledTooltip
        className={tooltipClassName}
        placement={tooltipPlacement}
        tooltipCoordinates={tooltipCoordinates}
        ref={tooltipRef}
        data-test-id={append(testId, 'content')}
      >
        {children}
      </StyledTooltip>
    </TooltipPortal>
  );
};

const CSSTop = css`
  &:after {
    left: 50%;
    border: 7px solid rgba(136, 183, 213, 0);
    border-top-color: ${({ theme: { colors } }) => colors?.nightBattleshipGrey};
    content: ' ';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    margin-left: -7px;
    top: 100%;
  }
`;

const CSSBottom = css`
  &:after {
    left: 50%;
    border: 7px solid rgba(136, 183, 213, 0);
    border-bottom-color: ${({ theme: { colors } }) =>
      colors?.nightBattleshipGrey};
    content: ' ';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    margin-left: -7px;
    bottom: 100%;
  }
`;

const KeyframesFadeIn = keyframes`
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
 `;

const CSSFadeIn = css`
  animation: ${KeyframesFadeIn} 0.5s;
`;

const StyledTooltip = styled.div<{
  placement: TooltipOverlayProps['placement'];
  tooltipCoordinates: {
    x: number;
    y: number;
  };
}>`
  position: absolute;
  padding: 10px;
  min-height: 34px;
  display: inline-block;
  color: ${({ theme: { colors } }) => colors?.white};
  background: ${({ theme: { colors } }) => colors?.nightBattleshipGrey};
  font-family: 'Open Sans', sans-serif;
  font-size: 12px;
  border-radius: 3px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.17);
  box-sizing: border-box;
  text-align: left;
  word-break: break-word;
  z-index: 99999;

  top: ${({ tooltipCoordinates }) => tooltipCoordinates.y}px;
  left: ${({ tooltipCoordinates }) => tooltipCoordinates.x}px;

  ${({ placement }) =>
    (placement === 'top' && CSSTop) || (placement === 'bottom' && CSSBottom)}

  ${CSSFadeIn}
`;
